import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const DerryChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Hypnotherapy Derry | Fibromyalgia Treatment Londonderry</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Derry/Londonderry. Online sessions recommended for convenience. In-person at Belfast clinic. Neuroscience-based approach for fibromyalgia, back pain, arthritis. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Derry, fibromyalgia hypnotherapy Londonderry, pain management Derry, back pain treatment Derry, arthritis pain Derry" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/derry-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Hypnotherapy Derry | Fibromyalgia Treatment Londonderry" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Derry/Londonderry. Neuroscience-based approach for fibromyalgia, back pain, arthritis." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Management Derry",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Derry/Londonderry via online sessions and Belfast clinic. Specializing in fibromyalgia, chronic back pain, arthritis, migraines, and neuropathic pain with NHS-complementary approach.",
            "url": "https://www.sharonclare-hypnotherapy.com/derry-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Management",
            "areaServed": {
              "@type": "City",
              "name": "Derry",
              "alternateName": "Londonderry",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "postalCode": "BT7 2FZ",
                  "addressRegion": "County Down",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can hypnotherapy really help with chronic pain in Derry?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is an evidence-based approach for chronic pain management. Research shows it can reduce pain intensity by working with the nervous system to close the pain gate and reduce central sensitization. A major review found hypnotherapy 68% more effective than no treatment for chronic pain. Many Derry residents find hypnotherapy helpful as a complement to NHS pain management while waiting for specialist appointments."
                }
              },
              {
                "@type": "Question",
                "name": "How does chronic pain hypnotherapy work with NHS care?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy is complementary to NHS care, not a replacement. With Western Trust pain clinic waiting times often extending to 6-12 months or longer, hypnotherapy can provide relief while you wait for NHS specialist appointments. Many Derry clients use hypnotherapy alongside their GP care, physiotherapy, or pain medication. Always inform your medical team about any complementary therapies you're using."
                }
              },
              {
                "@type": "Question",
                "name": "What chronic pain conditions do you treat for Derry clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I work with Derry clients experiencing fibromyalgia, chronic back pain, arthritis, migraines, neuropathic pain, IBS pain, and other persistent pain conditions. The neuroscience-based approach calms the nervous system, reduces pain perception, and helps your brain develop healthier pain processing patterns. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from both a medical and therapeutic perspective."
                }
              },
              {
                "@type": "Question",
                "name": "How can Derry residents access chronic pain treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Online sessions are the recommended option for Derry residents - you receive the same professional, evidence-based treatment from the comfort of your home without any travel, which is especially important when managing chronic pain. For those who prefer in-person sessions, the Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ is available. Most Derry clients choose online sessions for convenience and to avoid travel that may aggravate pain conditions."
                }
              },
              {
                "@type": "Question",
                "name": "What appointment times are available for Derry clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Online sessions offer flexible scheduling including daytime and evening appointments to suit your schedule. For in-person sessions at the Belfast clinic, flexible appointment times are also available. Online sessions are particularly popular with Derry clients as they allow you to prioritize pain management without taking time off work or managing the energy required for travel."
                }
              },
              {
                "@type": "Question",
                "name": "Can I access hypnotherapy while waiting for NHS pain clinic appointments?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Many Derry clients use hypnotherapy while on waiting lists for Western Trust pain clinic appointments, which often exceed 12 months. Hypnotherapy can provide relief during the waiting period and works alongside any NHS treatment you're currently receiving. Some clients find hypnotherapy so effective they continue using it as their primary pain management approach alongside medical care and physiotherapy."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Management for Derry/Londonderry
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Neuroscience-based hypnotherapy serving Derry via online sessions
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">30 miles</span> from Derry via A6 • <span className="font-semibold">35-40 min drive</span>
                </p>
              </div>
              <p className="text-gray-600">Online sessions available for Derry residents</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Derry
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Derry/Londonderry through convenient online sessions and two in-person clinic locations. The Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ, and the Lisburn clinic is at The Arzen Collective, 13B Antrim Street - clinic days and times vary week to week, with early evening appointments available. Online sessions provide the same professional, evidence-based treatment from the comfort of your home without any travel that might aggravate pain conditions. Most Derry clients choose online sessions for practical convenience, particularly given the lengthy NHS waiting times for Western Trust pain clinic appointments.
              </p>
              <p>
                Living with chronic pain in Derry comes with unique challenges. Long NHS waiting lists for pain management services often exceed 12 months, leaving many residents struggling with fibromyalgia, back pain, arthritis, migraines, or neuropathic pain while waiting for specialist support. The economic pressures facing the city, combined with the documented health legacy of post-conflict stress, create a perfect storm for chronic pain conditions to develop and persist.
              </p>
              <p>
                Modern neuroscience shows us that persistent pain is processed in the nervous system, and when we're stressed or anxious, pain signals intensify. This is where solution-focused hypnotherapy excels. By calming your fight-or-flight response, teaching your brain healthier pain processing patterns, and addressing the stress and anxiety that amplify pain, many clients experience significant reductions in pain levels and improvements in daily functioning.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I bring medical understanding to chronic pain treatment. I understand pain conditions from both a clinical and therapeutic perspective, and I know how frustrating it is when you're told "there's nothing more we can do" or "you'll just have to live with it." Hypnotherapy offers a science-backed approach that works alongside your medical care to help you regain control and quality of life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Chronic Pain */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              The Neuroscience of Chronic Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Persistent pain lasting beyond 12 weeks isn't "all in your head," but it is in your nervous system. Understanding how pain works helps explain why hypnotherapy can be so effective.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Shield className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Central Sensitization</h3>
                <p className="text-gray-700">
                  The spinal cord and brain become hypersensitive, amplifying normal signals and interpreting them as pain. This is why pain can feel disproportionate to actual tissue damage.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Fight-Flight-Freeze</h3>
                <p className="text-gray-700">
                  Ongoing stress keeps your survival system on high alert, which keeps pain signals turned up. Trauma, economic stress, and daily pressures all contribute to this heightened state.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Neuroplasticity</h3>
                <p className="text-gray-700">
                  The brain learns pain pathways like a well-worn motorway. The more they're used, the easier they fire, even with no new injury. But neuroplasticity also means we can create new, healthier pathways.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                This is why you can feel real, distressing pain even when medical scans look "normal." The pain is genuine - your brain is simply protecting you a little too well. Hypnotherapy works by calming this overactive alarm system and helping your brain interpret sensations differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy works directly with the mind-body connection to calm your overactive alarm system
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Shift from Danger to Safety</h3>
                    <p className="text-gray-700">
                      Calms the fight-flight response so your nervous system stops amplifying pain signals. This is particularly relevant for Derry residents dealing with historical trauma and ongoing community stress.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Lowers the intensity of messages traveling through the spinal cord to the brain, reducing pain perception through descending pain inhibition pathways.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Harness Neuroplasticity</h3>
                    <p className="text-gray-700">
                      Creates new, healthier neural pathways so your brain can learn to interpret sensations differently. The brain's ability to change means pain doesn't have to be permanent.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Practical Self-Management Tools</h3>
                    <p className="text-gray-700">
                      Self-hypnosis and relaxation techniques you can use at home to manage pain flare-ups, reduce stress, and improve sleep - all factors that influence pain levels.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                Research supports hypnotherapy's role in multidisciplinary pain care. A major review of over 7,000 children with chronic abdominal pain found hypnotherapy 68% more effective than no treatment - outperforming dietary and medication-based approaches.
              </p>
              <p className="text-gray-700 text-lg">
                For Derry residents waiting months for pain clinic appointments, hypnotherapy offers evidence-based support that can provide relief while you wait for NHS specialist care, or work alongside the treatment you're already receiving.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NHS-Complementary Positioning */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your Medical Care
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Western Trust pain clinic waiting times often exceeding 12 months, many Derry residents need support now while waiting for specialist appointments. Hypnotherapy can provide that bridge.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I work collaboratively with your GP, pain specialists, physiotherapists, and other healthcare providers. Many chronic pain clinics now recommend hypnotherapy alongside physiotherapy, medication, mindfulness, or gentle movement programs. By addressing the nervous system directly, hypnotherapy enhances these treatments and helps you regain confidence, mobility, and quality of life.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare team. I know the medications, the procedures, the specialist terminology, and the frustrations of chronic pain from both sides of the clinical encounter.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Medical Background</h3>
                <p className="text-gray-700">15 years Belfast City Hospital nursing experience brings clinical credibility to pain management</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">CNHC Registered</h3>
                <p className="text-gray-700">Professional registration ensures you receive evidence-based, ethical treatment</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">GP Collaboration</h3>
                <p className="text-gray-700">I work with your GP and medical team, not in competition with them</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Evidence-Based Approach</h3>
                <p className="text-gray-700">Neuroscience-backed techniques supported by research, not pseudoscience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Chronic Pain Conditions We Address
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Fibromyalgia</h3>
                <p className="text-gray-700">Widespread pain, fatigue, and sensitivity. Hypnotherapy addresses the nervous system sensitization underlying fibromyalgia symptoms.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Chronic Back Pain</h3>
                <p className="text-gray-700">Lower back pain, sciatica, disc problems. Calming stress and anxiety often significantly reduces back pain intensity.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Arthritis Pain</h3>
                <p className="text-gray-700">Osteoarthritis and rheumatoid arthritis pain management. Hypnotherapy helps reduce pain perception and improve mobility.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Migraines & Headaches</h3>
                <p className="text-gray-700">Chronic migraine, tension headaches. Stress reduction and trigger management through hypnotherapy can reduce frequency and intensity.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Neuropathic Pain</h3>
                <p className="text-gray-700">Nerve pain, burning sensations, numbness. Hypnotherapy works with pain gate control to reduce neuropathic pain signals.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">IBS & Abdominal Pain</h3>
                <p className="text-gray-700">Irritable bowel syndrome pain and discomfort. Gut-brain connection responds particularly well to hypnotherapy.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Derry Clients Choose Sharon Clare for Pain Management
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  15 Years Nursing Experience
                </h3>
                <p className="text-gray-700">
                  With 15 years at Belfast City Hospital, I understand pain conditions from a medical perspective. I've seen firsthand how chronic pain affects patients and families, and I know the limitations of medication-only approaches. This medical background brings credibility and understanding to chronic pain treatment that many Derry clients find reassuring.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Understanding Historical Trauma & Pain
                </h3>
                <p className="text-gray-700">
                  Growing up and working in Northern Ireland, I understand how historical trauma, community stress, and socioeconomic pressures contribute to chronic pain. Research shows clear links between post-conflict trauma and chronic pain conditions. The solution-focused approach helps you move forward from these historical patterns while addressing current pain.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Weekend Availability
                </h3>
                <p className="text-gray-700">
                  Flexible online appointment scheduling provides maximum convenience for Derry residents managing work commitments alongside chronic pain. Online sessions are particularly popular as they allow you to focus on pain management without taking time off work or adding travel stress to your week. In-person sessions are also available at the Belfast clinic for those who prefer face-to-face treatment.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Online Sessions Available
                </h3>
                <p className="text-gray-700">
                  If traveling 30 miles is difficult due to your pain condition, secure online sessions via video call provide the same professional treatment from your home in Derry. This option is ideal for clients experiencing pain flare-ups, mobility limitations, or those who find traveling exacerbates their pain. The therapeutic effectiveness is equivalent to in-person sessions.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Peaceful Rural Setting
                </h3>
                <p className="text-gray-700">
                  For those who prefer in-person sessions, the Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road provides a professional, calming environment. However, most Derry clients find online sessions ideal for chronic pain management - there's no travel to aggravate pain, and the comfort of your own home supports relaxation. The familiar, safe environment reinforces the mind-body connection central to effective pain management.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Evidence-Based Neuroscience Approach
                </h3>
                <p className="text-gray-700">
                  Solution-focused hypnotherapy combines the latest pain neuroscience research with proven therapeutic techniques. Rather than dwelling on pain history, we focus on solutions, building resilience, and practical strategies for managing pain. This forward-focused approach resonates with Derry clients who want to move beyond pain and reclaim their lives.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Accessing Pain Treatment from Derry
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <MapPin className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">Online Sessions (Recommended for Derry Clients)</h3>
                  <p className="text-gray-700 mb-4">
                    Online sessions are the most convenient and practical option for Derry residents managing chronic pain. You receive exactly the same professional, evidence-based treatment via secure video call - without any travel that might aggravate your pain condition.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li>No travel time - attend from the comfort of your home in Derry</li>
                    <li>No risk of pain flare-ups from long journeys</li>
                    <li>Flexible scheduling including evenings and weekends</li>
                    <li>Same therapeutic effectiveness as in-person sessions</li>
                  </ul>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">The Practical Choice for Pain Management</h3>
                <p className="text-gray-700">
                  Most Derry clients choose online sessions - particularly when managing chronic pain, travel can be exhausting and may worsen symptoms. Online sessions mean you can attend when you're most comfortable, without the added physical burden of a long journey. The relaxation of being in your own home actually supports the therapeutic process.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">In-Person Sessions at Belfast Clinic</h3>
              <p className="text-gray-700">
                If you prefer face-to-face treatment, in-person sessions are available at my Belfast clinic, Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ. While Belfast is approximately 70 miles from Derry, some clients value the in-person experience for their initial consultation and then continue with online sessions for ongoing treatment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Derry Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with fibromyalgia for 8 years, I'd tried everything the NHS offered. Sharon's online hypnotherapy sessions were so convenient from home in Derry - her approach actually helped where nothing else had. My pain levels have reduced significantly and I'm sleeping better. Her nursing background meant she really understood my condition."
                </p>
                <p className="font-semibold text-gray-900">M.D., Derry City - Fibromyalgia</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The online sessions were perfect for managing my back pain from home - no long journey to worsen symptoms. Sharon helped me understand how stress was amplifying my pain. After 6 sessions, I've reduced my painkillers significantly and I'm functioning so much better. Best decision I made."
                </p>
                <p className="font-semibold text-gray-900">P.K., Waterside, Derry - Chronic Back Pain</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Waiting 18 months for the pain clinic was unbearable. Sharon's online hypnotherapy gave me tools to manage my arthritis pain while I waited. The flexible online appointments meant I didn't have to take time off work. My pain hasn't disappeared but it's manageable now, and I feel in control again."
                </p>
                <p className="font-semibold text-gray-900">T.B., Limavady - Arthritis Pain</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Derry Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy really help with chronic pain?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is an evidence-based approach for chronic pain management supported by substantial research. Studies show it can reduce pain intensity by working with the nervous system to close the pain gate and reduce central sensitization. A major review found hypnotherapy 68% more effective than no treatment for chronic pain. Many Derry residents find hypnotherapy helpful as a complement to NHS pain management, particularly while waiting for specialist appointments which can take 12+ months.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does hypnotherapy work with my GP and NHS treatment?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. I work collaboratively with your GP and medical team. Many Derry clients use hypnotherapy while on waiting lists for Western Trust pain clinic appointments, or alongside physiotherapy and medication. With my 15 years nursing experience, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare providers. Always inform your medical team about any complementary therapies you're using.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What pain conditions do you treat for Derry clients?
                </h3>
                <p className="text-gray-700">
                  I work with Derry clients experiencing fibromyalgia, chronic back pain, arthritis, migraines and tension headaches, neuropathic pain, IBS pain, and other persistent pain conditions. The neuroscience-based approach calms the nervous system, reduces pain perception through descending pain inhibition, and helps your brain develop healthier pain processing patterns. The approach is particularly effective for pain conditions with a strong stress component.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy replace my pain medication?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy works alongside your medical treatment, not as a replacement. Many clients find they can reduce medication under medical supervision as their pain levels decrease, but any changes to medication must always be discussed with your doctor. The goal is to give you additional tools for pain management that work with your medical care, not to replace necessary medication.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need for chronic pain?
                </h3>
                <p className="text-gray-700">
                  The number of sessions varies based on individual circumstances. Most clients see improvement within 6-8 sessions for chronic pain management, though some notice changes sooner. Complex pain conditions or long-standing pain may benefit from 8-10 sessions. We'll create a personalized treatment plan during your initial consultation based on your specific pain condition, how long you've had it, and your goals.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Are online sessions effective for chronic pain management?
                </h3>
                <p className="text-gray-700">
                  Yes, online sessions are just as effective as in-person sessions for chronic pain management, and they're the recommended option for Derry residents. There's no travel to aggravate your pain condition, and you can attend from the comfort of your own home when you're most comfortable. If you prefer in-person sessions, the Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road is available. The flexibility to choose between online and in-person sessions based on your needs is always available.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore how clinical hypnotherapy can help with chronic pain, book your initial consultation today and start retraining your brain to turn down the volume on pain."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default DerryChronicPain;
